import { useTranslation } from 'react-i18next';
import type { MapLegendRow } from './MapLegend';

/**
 * A property's printable map sheet.
 *
 * PRESENTATIONAL only — props in, markup out. No data fetching, no
 * `apiClient` import (directly or transitively), so it is reused as-is by the
 * public/unauthenticated client share page. The caller (`PrintMapButton`,
 * `SharePage`) is responsible for loading brand settings, resolving the
 * static map image URL(s), and building `devices`/`legendRows`/`sections`.
 *
 * Deliberately styled like `components/report/*`: literal light colors via
 * the `.map-sheet-*` classes in `index.css`, never the dark-mode-aware
 * theme tokens — this is a document meant to be printed or handed to a
 * client, so it stays light in every app theme (see AGENTS.md "Theming").
 */

export type PropertyMapSheetDeviceType = 'controller' | 'water_meter' | 'double_check' | 'zone';

export interface PropertyMapSheetDevice {
  id: string;
  type: PropertyMapSheetDeviceType;
  /** Already-composed descriptive name/number, e.g. "Zone 3" or "Meter #12". */
  name: string;
  /** Short, non-sensitive location/notes text, e.g. "Backyard, north bed". */
  detail?: string | null;
  /** The label on this device's map pin ("C3", "M1"), or null when it has no
   *  GPS location and so isn't pinned. Leave undefined on every device to hide
   *  the Pin column (the public share page). */
  pinLabel?: string | null;
}

/** A pin drawn over a map image, in the image's logical px (see
 *  `lib/propertyMapPrint.ts`). */
export interface PropertyMapSheetPin {
  key: string;
  x: number;
  y: number;
  /** True location when the pin was fanned out from a crowded spot. */
  anchorX?: number;
  anchorY?: number;
  color: string;
  /** Empty = a plain dot: large for the property pin (key "property"),
   *  small for anything else (overview zones when close-ups are on). */
  label: string;
}

export interface PropertyMapSheetMap {
  imageUrl: string;
  alt: string;
  /** Logical size the pins' coordinates are expressed in (the image's
   *  `size=` before `scale`). */
  width: number;
  height: number;
  pins: PropertyMapSheetPin[];
  onLoad?: () => void;
  onError?: () => void;
}

export interface PropertyMapSheetZoneRow {
  id: string;
  /** Null = no GPS location, so not on the map. */
  pinLabel: string | null;
  zoneNumber: number;
  zoneType?: string | null;
  location?: string | null;
}

/** A controller's zones: an optional close-up map, then the zone table. */
export interface PropertyMapSheetSection {
  id: string;
  title: string;
  subtitle?: string | null;
  map?: PropertyMapSheetMap | null;
  zones: PropertyMapSheetZoneRow[];
  /** Start the section on a new printed page (used with close-up maps). */
  pageBreak?: boolean;
}

export interface PropertyMapSheetProps {
  companyName?: string | null;
  logoUrl?: string | null;
  propertyName: string;
  address?: string | null;
  /** Pre-formatted, locale-aware date string — the sheet does no date math of
   *  its own so it stays usable from any caller/locale. */
  printedDateLabel: string;
  mapImageUrl: string;
  mapImageAlt: string;
  onMapImageLoad?: () => void;
  onMapImageError?: () => void;
  devices: PropertyMapSheetDevice[];
  /** Rows for the static legend, in the SAME color constants used to build
   *  the map markers (`DEVICE_COLORS` / `PROPERTY_MARKER_COLOR`) so the
   *  legend can never drift from the image. */
  legendRows: MapLegendRow[];
  /** Labelled pins drawn over `mapImageUrl` (which is then a marker-free
   *  image at a known view). Omit when the image already carries its markers
   *  (the public share page). */
  mapPins?: PropertyMapSheetPin[];
  /** Logical size `mapPins` are expressed in. Default 640 x 400. */
  mapSize?: { width: number; height: number };
  /** Plain-text notes under the legend, e.g. how many zones aren't pinned. */
  notices?: string[];
  /** Zone tables per controller, after the device table. */
  sections?: PropertyMapSheetSection[];
  /** Overrides the device table heading (default "Devices"). */
  devicesTitle?: string;
  className?: string;
}

// Exported so callers building `legendRows` (see `PrintMapButton`) use the
// exact same i18n keys as the device table, and the two can never drift.
export const DEVICE_TYPE_LABEL_KEY: Record<PropertyMapSheetDeviceType, string> = {
  controller: 'mapPrint.typeController',
  zone: 'mapPrint.typeZone',
  water_meter: 'mapPrint.typeWaterMeter',
  double_check: 'mapPrint.typeDoubleCheck',
};

const PIN_FONT_SIZE = 8;
const PIN_HEIGHT = 12;

/** Labelled pins over a map image. The SVG shares the image's aspect ratio and
 *  logical coordinate space, so it scales with the image at any print size. */
function PinOverlay({ pins, width, height }: { pins: PropertyMapSheetPin[]; width: number; height: number }) {
  return (
    <svg className="map-sheet-pin-overlay" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true">
      {pins.map((p) =>
        p.anchorX !== undefined && p.anchorY !== undefined ? (
          <g key={`${p.key}-leader`}>
            <line x1={p.anchorX} y1={p.anchorY} x2={p.x} y2={p.y} stroke="#ffffff" strokeWidth={1} />
            <circle cx={p.anchorX} cy={p.anchorY} r={1.75} fill="#ffffff" />
          </g>
        ) : null,
      )}
      {pins.map((p) => {
        if (!p.label) {
          const isProperty = p.key === 'property';
          return (
            <circle
              key={p.key}
              cx={p.x}
              cy={p.y}
              r={isProperty ? 6 : 3.5}
              fill={p.color}
              stroke="#ffffff"
              strokeWidth={isProperty ? 2 : 1}
            />
          );
        }
        const w = Math.max(PIN_HEIGHT + 2, p.label.length * 5.2 + 6);
        return (
          <g key={p.key}>
            <rect
              x={p.x - w / 2}
              y={p.y - PIN_HEIGHT / 2}
              width={w}
              height={PIN_HEIGHT}
              rx={PIN_HEIGHT / 2}
              fill={p.color}
              stroke="#ffffff"
              strokeWidth={1.25}
            />
            <text
              x={p.x}
              y={p.y}
              fill="#ffffff"
              fontSize={PIN_FONT_SIZE}
              fontWeight={700}
              fontFamily="Arial, Helvetica, sans-serif"
              textAnchor="middle"
              dominantBaseline="central"
            >
              {p.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

interface MapImageProps {
  imageUrl: string;
  alt: string;
  pins?: PropertyMapSheetPin[];
  width: number;
  height: number;
  onLoad?: () => void;
  onError?: () => void;
}

function MapImage({ imageUrl, alt, pins, width, height, onLoad, onError }: MapImageProps) {
  return (
    <div className="map-sheet-map-wrap">
      <img src={imageUrl} alt={alt} className="map-sheet-map-img" onLoad={onLoad} onError={onError} />
      {pins && pins.length > 0 ? <PinOverlay pins={pins} width={width} height={height} /> : null}
    </div>
  );
}

function PinCell({ label, notPinnedText }: { label: string | null; notPinnedText: string }) {
  return label ? (
    <span className="map-sheet-pin-chip">{label}</span>
  ) : (
    <span className="map-sheet-not-pinned">{notPinnedText}</span>
  );
}

export function PropertyMapSheet({
  companyName,
  logoUrl,
  propertyName,
  address,
  printedDateLabel,
  mapImageUrl,
  mapImageAlt,
  onMapImageLoad,
  onMapImageError,
  devices,
  legendRows,
  mapPins,
  mapSize,
  notices,
  sections,
  devicesTitle,
  className,
}: PropertyMapSheetProps) {
  const { t } = useTranslation('common');
  const size = mapSize ?? { width: 640, height: 400 };
  const showPinColumn = devices.some((d) => d.pinLabel !== undefined);
  const notPinnedText = t('mapPrint.notPinned');

  return (
    <div className={`map-sheet${className ? ` ${className}` : ''}`}>
      <div className="map-sheet-header">
        <div className="map-sheet-brand">
          {logoUrl ? (
            <img src={logoUrl} alt={companyName || t('mapPrint.logoAlt')} className="map-sheet-logo" />
          ) : null}
          <div>
            <div className="map-sheet-company">{companyName || ''}</div>
            <div className="map-sheet-printed">{t('mapPrint.printedOn', { date: printedDateLabel })}</div>
          </div>
        </div>
        <div className="map-sheet-property">
          <div className="map-sheet-property-name">{propertyName}</div>
          {address ? <div className="map-sheet-property-address">{address}</div> : null}
        </div>
      </div>

      <MapImage
        imageUrl={mapImageUrl}
        alt={mapImageAlt}
        pins={mapPins}
        width={size.width}
        height={size.height}
        onLoad={onMapImageLoad}
        onError={onMapImageError}
      />

      {legendRows.length > 0 && (
        <div className="map-sheet-legend">
          <div className="map-sheet-legend-title">{t('mapPrint.legendTitle')}</div>
          <div className="map-sheet-legend-rows">
            {legendRows.map((row) => (
              <div key={row.key} className="map-sheet-legend-row">
                <span
                  className={`map-sheet-legend-swatch map-sheet-legend-swatch-${row.shape}`}
                  style={{ backgroundColor: row.shape === 'line' ? undefined : row.color, borderColor: row.color }}
                />
                <span>{row.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {notices && notices.length > 0 && (
        <div className="map-sheet-notices">
          {notices.map((n) => (
            <p key={n}>{n}</p>
          ))}
        </div>
      )}

      <div className="map-sheet-devices">
        <div className="map-sheet-devices-title">{devicesTitle ?? t('mapPrint.devicesTitle')}</div>
        {devices.length === 0 ? (
          <p className="map-sheet-devices-empty">{t('mapPrint.noDevices')}</p>
        ) : (
          <table className="map-sheet-devices-table">
            <thead>
              <tr>
                {showPinColumn && <th>{t('mapPrint.colPin')}</th>}
                <th>{t('mapPrint.colType')}</th>
                <th>{t('mapPrint.colName')}</th>
                <th>{t('mapPrint.colDetail')}</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((device) => (
                <tr key={device.id}>
                  {showPinColumn && (
                    <td>
                      <PinCell label={device.pinLabel ?? null} notPinnedText={notPinnedText} />
                    </td>
                  )}
                  <td>{t(DEVICE_TYPE_LABEL_KEY[device.type])}</td>
                  <td>{device.name}</td>
                  <td>{device.detail || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {sections?.map((section) => (
        <div key={section.id} className={`map-sheet-section${section.pageBreak ? ' map-sheet-section-break' : ''}`}>
          <div className="map-sheet-section-header">
            <div className="map-sheet-section-title">{section.title}</div>
            {section.subtitle ? <div className="map-sheet-section-subtitle">{section.subtitle}</div> : null}
          </div>
          {section.map ? (
            <MapImage
              imageUrl={section.map.imageUrl}
              alt={section.map.alt}
              pins={section.map.pins}
              width={section.map.width}
              height={section.map.height}
              onLoad={section.map.onLoad}
              onError={section.map.onError}
            />
          ) : null}
          <table className="map-sheet-devices-table">
            <thead>
              <tr>
                <th>{t('mapPrint.colPin')}</th>
                <th>{t('mapPrint.colZone')}</th>
                <th>{t('mapPrint.colZoneType')}</th>
                <th>{t('mapPrint.colLocation')}</th>
              </tr>
            </thead>
            <tbody>
              {section.zones.map((z) => (
                <tr key={z.id}>
                  <td>
                    <PinCell label={z.pinLabel} notPinnedText={notPinnedText} />
                  </td>
                  <td>{t('mapPrint.zoneNumber', { n: z.zoneNumber })}</td>
                  <td>{z.zoneType || '—'}</td>
                  <td>{z.location || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
